import { Card, CardContent } from "@/components/ui/card";
import { ChevronDown, ChevronUp } from "lucide-react";
import { useState } from "react";

interface Column {
  header: string;
  accessor: string;
  align?: 'left' | 'right' | 'center';
  width?: string;
  editable?: boolean;
  columnType?: 'text' | 'number' | 'select' | 'date' | 'select-with-other' | 'country';
  options?: string[];
  optionsWithIcons?: Array<{ value: string; icon: any }>;
  primaryField?: boolean; // New: Mark fields to show in collapsed view
  showTooltip?: boolean;
  tooltipAccessor?: string;
}

interface RefundItemCardProps {
  item: any;
  columns: Column[];
  index: number;
}

export const RefundItemCard: React.FC<RefundItemCardProps> = ({ item, columns, index }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  // Separate primary fields (shown when collapsed) from other fields
  const primaryColumns = columns.filter(col => col.primaryField);
  const secondaryColumns = columns.filter(col => !col.primaryField);

  const renderField = (column: Column, isCompact: boolean = false) => {
    const value = item[column.accessor];

    // Skip rendering if value is null, undefined, or empty string
    if (value === null || value === undefined || value === '') {
      return null;
    }

    return (
      <div key={column.accessor} className="flex justify-between items-start gap-2">
        <div className={`${isCompact ? 'text-base' : 'text-sm'} font-medium text-muted-foreground ${isCompact ? 'min-w-0' : 'min-w-[100px]'}`}>
          {column.header}
        </div>
        <div className={`${isCompact ? 'text-base' : 'text-sm'} font-semibold text-right flex-1`}>
          {typeof value === 'number' ? value.toFixed(2) : value}
        </div>
      </div>
    );
  };

  return (
    <Card className="mb-3">
      <CardContent className="pt-4 pb-4 px-4">
        {/* Primary fields - always visible */}
        <div className="space-y-3">
          {primaryColumns.map((column) => renderField(column, true))}
        </div>

        {/* Expand/Collapse button */}
        {secondaryColumns.length > 0 && (
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="flex items-center gap-1 text-sm text-primary hover:underline mt-3 w-full justify-center"
          >
            {isExpanded ? (
              <>
                <span>Show less</span>
                <ChevronUp className="h-4 w-4" />
              </>
            ) : (
              <>
                <span>Show more</span>
                <ChevronDown className="h-4 w-4" />
              </>
            )}
          </button>
        )}

        {/* Secondary fields - shown when expanded */}
        {isExpanded && secondaryColumns.length > 0 && (
          <div className="space-y-3 mt-3 pt-3 border-t">
            {secondaryColumns.map((column) => renderField(column))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
